import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Pie } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';

// Register the components
Chart.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const DepartmentProjectsChart = () => {
    const [departments, setDepartments] = useState([]);
    const [selectedDeptId, setSelectedDeptId] = useState('');
    const [projectTypeData, setProjectTypeData] = useState({});
    const [totalProjectCount, setTotalProjectCount] = useState(0);
    const [filteredProjectCount, setFilteredProjectCount] = useState(0);
    const [loading, setLoading] = useState(true);

    // Fetch departments when the component mounts
    useEffect(() => {
        fetchDepartments();
        fetchProjectCounts(); // Fetch overall project counts initially
    }, []);

    // Fetch departments function
    const fetchDepartments = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/departments`);
            setDepartments(response.data);
        } catch (error) {
            console.error('Error fetching departments:', error);
        }
    };

    // Fetch overall project counts function
    const fetchProjectCounts = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/projects/countByType`);
            const projectsByType = response.data;

            if (projectsByType) {
                const labels = Object.keys(projectsByType);
                const data = Object.values(projectsByType);

                setProjectTypeData({
                    labels: labels,
                    datasets: [
                        {
                            label: 'Projects by Type',
                            data: data,
                            backgroundColor: [
                                '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'
                            ],
                            hoverBackgroundColor: [
                                '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'
                            ]
                        }
                    ]
                });

                // Calculate total project count
                setTotalProjectCount(Object.values(projectsByType).reduce((a, b) => a + b, 0));
            } else {
                console.error('Error: projectsByType is undefined or null');
            }
        } catch (error) {
            console.error('Error fetching project counts:', error);
        } finally {
            setLoading(false); // Set loading to false after fetching data
        }
    };

    // Fetch project counts by department ID
    const fetchProjectCountsByDept = async (deptId) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/project-departments/project-type-count/${deptId}`);
            const projectCounts = response.data;

            const labels = Object.keys(projectCounts); // Get project types
            const data = Object.values(projectCounts); // Get counts

            setProjectTypeData({
                labels: labels,
                datasets: [
                    {
                        label: 'Project Type Count',
                        data: data,
                        backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'],
                    }
                ]
            });

            // Update filtered project count for the selected department
            setFilteredProjectCount(Object.values(projectCounts).reduce((a, b) => a + b, 0));

        } catch (error) {
            console.error('Error fetching project type counts by department:', error);
        }
    };

    // Handle department selection change
    const handleChartChange = async (event) => {
        const deptId = event.target.value;
        setSelectedDeptId(deptId);

        if (deptId) {
            await fetchProjectCountsByDept(deptId); // Fetch counts by department
        } else {
            await fetchProjectCounts(); // Fetch overall counts if no department selected
            setFilteredProjectCount(0); // Reset the filtered project count if "All Departments" is selected
        }
    };

    // Debugging log to verify chart data
    console.log('Project Type Data:', projectTypeData);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false, // Allows the chart to take full height and width of the container
        plugins: {
            legend: {
                position: 'right', // Aligns labels properly
            },
        },
    };

    return (
        <div className='mt-2'>
            <div style={{ marginBottom: '50px' }}>
                <h6 style={{marginBottom:'12px'}}>Filter by Department:</h6>
                <select 
                    onChange={handleChartChange} 
                    value={selectedDeptId}  
                    className="form-select w-100 mb-0"  
                    style={{ marginBottom: '10px' }}
                >
                    <option value="">All Departments</option>
                    {departments.map(department => (
                        <option key={department.deptId} value={department.deptId}>
                            {department.deptName}
                        </option>
                    ))}
                </select>

                {/* Display total count for all departments */}
                <div className="col-md-10 mt-3 text-center">

                    {/* Display filtered count when a department is selected */}
                    {selectedDeptId && <h5>Project Count for {departments.find(dept => dept.deptId === selectedDeptId)?.deptName}: {filteredProjectCount}</h5>}
                </div>

                <div className="col-md-10">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '300px', width: '100%' }}>
                        {loading ? (
                            <p>Loading chart data...</p>
                        ) : projectTypeData.labels && projectTypeData.labels.length > 0 ? (
                            <div style={{ height: '100%', width: '100%', maxWidth: '600px' }}>
                                <Pie data={projectTypeData} options={chartOptions} />
                            </div>
                        ) : (
                            <p>No data available</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DepartmentProjectsChart;
